import type { ComponentProps } from 'react';

import { cn } from '../cn';
import type { ColorName, ColorVariant, Style } from './buttonClasses';
import { composeVariant } from './buttonClasses';
import type { Size } from './types';
import { sizeFont, sizePaddingL } from './types';

export type LabelProps = ComponentProps<'label'> & {
  size?: Size;
  /** Color axis — `'blue' | 'red' | 'green' | …`. Defaults to `'gray'`. */
  color?: ColorName;
  /** Style axis — `'filled' | 'outline' | 'soft'`. Defaults to `'filled'`. */
  tone?: Style;
  /** Add left padding to align label text with input text */
  alignWithInput?: boolean;
};

function labelVariantClasses(variant: ColorVariant) {
  if (variant === 'blue') return 'text-astronaut-600 dark:text-astronaut-400';
  if (variant === 'red') return 'text-fair-pink-600 dark:text-fair-pink-400';
  if (variant === 'green') return 'text-mantis-600 dark:text-mantis-400';
  if (variant === 'yellow') return 'text-quarter-spanish-white-600 dark:text-quarter-spanish-white-400';
  if (variant === 'purple') return 'text-purple-600 dark:text-purple-400';
  // default
  return 'text-slate-700 dark:text-slate-300';
}

export default function Label({
  size = 'md',
  color,
  tone,
  alignWithInput,
  className,
  children,
  ...props
}: LabelProps) {
  const effectiveVariant: ColorVariant = composeVariant(color ?? 'gray', tone ?? 'filled');
  return (
    <label
      className={cn(
        'font-sans font-medium',
        labelVariantClasses(effectiveVariant),
        sizeFont(size),
        alignWithInput && sizePaddingL(size),
        className,
      )}
      {...props}>
      {children}
    </label>
  );
}
